import React, { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { useData } from '@hooks/useData';
import { useLogLocation } from '@services/useLogLocation';

export const UserGeoLocation = () => {
    const { allSettings, login, user } = useData();
    const { request: requestLogLocation } = useLogLocation();
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [resumeKey, setResumeKey] = useState(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [geoError, setGeoError] = useState<string | null>(null);

    // holds the most recent data
    const stateRef = useRef({ latitude, longitude, loading, shareLocation: user?.data?.share_location, requestLogLocation });
    stateRef.current = { latitude, longitude, loading, shareLocation: user?.data?.share_location, requestLogLocation };

    // useful for when user resumes the application
    const skipNextLocationLog = useRef(false);

    // check if location logging was called after login
    const hasLoggedAfterLogin = useRef(false);

    useEffect(() => {
        if (!navigator.geolocation) {
            setGeoError('Geolocation not supported');
            setLoading(false);
            return;
        }

        const success = (position: GeolocationPosition) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            setLatitude(lat);
            setLongitude(lng);
            setLoading(false);

            // after user resumes app
            // restart logging as soon as the GPS has a fresh position
            if (skipNextLocationLog.current) {
                skipNextLocationLog.current = false;
                const { shareLocation, requestLogLocation: logLocation } = stateRef.current;
                if (shareLocation) logLocation({ latitude: lat, longitude: lng });
            }
        };

        const fail = (err: GeolocationPositionError) => {
            setGeoError(err.message);
            setLoading(false);
        };

        const watcherId = navigator.geolocation.watchPosition(success, fail, {
            enableHighAccuracy: false,
            maximumAge: 1000,
            timeout: 60000,
        });

        // handle when app resumes
        // use stateRef to avoid stale data
        // 1 second time check prevents a double call when visibilitychange and focus both fire (common on mobile)
        let lastResumeAt = 0;

        const handleResume = () => {
            const now = Date.now();
            if (now - lastResumeAt < 1000) return;
            lastResumeAt = now;

            skipNextLocationLog.current = true;
            // restart the interval so it counts from the moment the user resumed
            setResumeKey(k => k + 1);
        };

        // visibilitychange covers most browsers
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') handleResume();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        // window focus is a fallback for iOS Safari PWAs
        window.addEventListener('focus', handleResume);

        return () => {
            navigator.geolocation.clearWatch(watcherId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleResume);
        };
    }, []);

    // right after login, also request to log location
    useEffect(() => {
        if (!login) {
            hasLoggedAfterLogin.current = false;
            return;
        }
        if (hasLoggedAfterLogin.current) return;
        if (!user?.data?.share_location || loading || latitude == null || longitude == null) return;

        hasLoggedAfterLogin.current = true;
        requestLogLocation({ latitude, longitude });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login, user?.data?.share_location, loading, latitude, longitude]);

    useEffect(() => {
        const intervalMinutes = Number(allSettings?.data?.locationUpdateInterval) || 0;
        const intervalMs = intervalMinutes * 60 * 1000;

        if (!login || intervalMinutes === 0) {
            return;
        }

        const intervalId = setInterval(() => {
            if (user?.data?.share_location) {
                if (loading || latitude == null || longitude == null) {
                    return;
                }
                requestLogLocation({
                    latitude: latitude,
                    longitude: longitude,
                });
            }
        }, intervalMs);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.data?.share_location, latitude, longitude, allSettings?.data, resumeKey]);

    return (
        <>
            <Outlet />
        </>
    );
};
